import React from "react";

type Props = { children: React.ReactNode };
type State = { error: Error | null };

/**
 * Without this, any render-time throw unmounts the whole React tree and the
 * user just sees a white screen with no clue what happened. This catches it,
 * shows the actual message, and — in development — the component stack.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the full stack in the console for reporting.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-muted px-5 py-16 font-manrope">
        <div className="w-full max-w-[560px] rounded-xl border bg-background p-6 text-center">
          <h1 className="font-sora text-[20px] font-bold text-foreground">
            Something went wrong
          </h1>
          <p className="mt-2 text-[14px] font-medium text-muted-foreground">
            This screen hit an unexpected error. Reloading usually clears it.
          </p>

          <pre className="mt-4 max-h-[220px] overflow-auto rounded-lg bg-muted p-3 text-left font-mono text-[12px] text-destructive">
            {error.message || String(error)}
          </pre>

          <div className="mt-6 flex flex-col justify-center gap-2 mds:flex-row">
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="h-11 rounded-xl border bg-background px-5 text-[14px] font-semibold text-foreground transition-colors hover:bg-muted"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.assign("/dashboard")}
              className="h-11 rounded-xl bg-primary px-5 text-[14px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
